import React from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RemovableBadge } from '@/components/ui/removable-badge';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { CADUploadZone, CADModelList } from '../../components/cad';
import { CADModel } from '../../types/cad';

interface CadViewerSidebarProps {
  models: CADModel[];
  selectedModel: CADModel | null;
  comparisonModel: CADModel | null;
  viewerMode: 'single' | 'compare';
  comparing: boolean;
  onUploadComplete: (model: CADModel) => void;
  onSelect: (model: CADModel) => void;
  onDelete: (modelId: string) => void;
  onEditClassification: (model: CADModel) => void;
  onSetComparisonTarget: (model: CADModel) => void;
  onClearComparisonTarget: () => void;
  onStartComparison: () => void;
}

/**
 * Left column: upload zone, the uploaded-model list, and the comparison
 * controls that appear once a target is picked. Markup is unchanged from the
 * inline block it replaced.
 */
const CadViewerSidebar: React.FC<CadViewerSidebarProps> = ({
  models,
  selectedModel,
  comparisonModel,
  viewerMode,
  comparing,
  onUploadComplete,
  onSelect,
  onDelete,
  onEditClassification,
  onSetComparisonTarget,
  onClearComparisonTarget,
  onStartComparison,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-[280px] shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
      <div className="p-3">
        <CADUploadZone onUploadComplete={onUploadComplete} />
      </div>
      <Separator />
      <div className="p-3">
        <span className="text-xs font-semibold text-muted-foreground">
          {t('cad.uploadedModels', { count: models.length })}
        </span>
      </div>
      <div className="flex-1 overflow-auto px-2">
        <CADModelList
          models={models}
          selectedModelId={selectedModel?.id || null}
          onSelect={onSelect}
          onDelete={onDelete}
          onEditClassification={onEditClassification}
          onSetComparisonTarget={onSetComparisonTarget}
          comparisonModelId={comparisonModel?.id || null}
        />
      </div>
      {/* Comparison controls */}
      {selectedModel && comparisonModel && viewerMode === 'single' && (
        <div className="border-t border-border p-3">
          <span className="mb-1 block text-xs text-muted-foreground">
            {t('cad.comparisonTarget')}
          </span>
          <RemovableBadge
            onRemove={onClearComparisonTarget}
            removeLabel={`${comparisonModel.filename} — ${t('common.clear')}`}
            className="mb-2 max-w-full overflow-hidden"
          >
            <span className="min-w-0 truncate">{comparisonModel.filename}</span>
          </RemovableBadge>
          <Button
            size="sm"
            className="w-full"
            onClick={onStartComparison}
            disabled={comparing}
          >
            {comparing ? <Spinner size="sm" /> : <Columns2 />}
            {comparing ? t('cad.comparing') : t('cad.startComparison')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CadViewerSidebar;
